import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import AdminApp from './admin/AdminApp';
import { isAdminHostname } from './admin/isAdminHost';

// Lazy load non-critical pages to reduce initial bundle size
const About = lazy(() => import('./pages/About'));
const MoviePage = lazy(() => import('./pages/MoviePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const Support = lazy(() => import('./pages/Support'));
const PlayerPage = lazy(() => import('./pages/PlayerPage'));
const ChannelPage = lazy(() => import('./pages/ChannelPage'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// Loading component for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-purple mx-auto mb-4"></div>
      <p className="text-white/80">Loading...</p>
    </div>
  </div>
);

function App() {
  const navigate = useNavigate();
  const isAdmin = isAdminHostname();
  const [movies, setMovies] = useState([]);
  const [moviesLoaded, setMoviesLoaded] = useState(false);

  useEffect(() => {
    if (isAdmin) return;
    // Defer non-critical data loading until after initial render
    const loadMovies = async () => {
      try {
        // Use requestIdleCallback to load movies when browser is idle
        if (window.requestIdleCallback) {
          requestIdleCallback(async () => {
            const { fetchMovies } = await import('./services/movieApi');
            const movieData = await fetchMovies();
            setMovies(movieData);
            setMoviesLoaded(true);
          });
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(async () => {
            const { fetchMovies } = await import('./services/movieApi');
            const movieData = await fetchMovies();
            setMovies(movieData);
            setMoviesLoaded(true);
          }, 1000);
        }
      } catch (error) {
        console.error('Error loading movies for search:', error);
        setMoviesLoaded(true); // Mark as loaded even if failed
      }
    };

    loadMovies();
  }, [isAdmin]);

  if (isAdmin) {
    return <AdminApp />;
  }

  return (
    <div className="min-h-screen">
      <Routes>
        <Route path="/player/:videoId" element={
          <Suspense fallback={<PageLoader />}>
            <PlayerPage />
          </Suspense>
        } />
        <Route path="*" element={
          <>
            <Navbar 
              movies={movies} 
              moviesLoaded={moviesLoaded}
              onPlay={(video) => navigate(`/player/${video.id}`)} 
            />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={
                <Suspense fallback={<PageLoader />}>
                  <About />
                </Suspense>
              } />
              <Route path="/movie/:id" element={
                <Suspense fallback={<PageLoader />}>
                  <MoviePage />
                </Suspense>
              } />
              <Route path="/search" element={
                <Suspense fallback={<PageLoader />}>
                  <SearchPage />
                </Suspense>
              } />
              <Route path="/support" element={
                <Suspense fallback={<PageLoader />}>
                  <Support />
                </Suspense>
              } />
              <Route path="/channel/:channelName" element={
                <Suspense fallback={<PageLoader />}>
                  <ChannelPage />
                </Suspense>
              } />
              <Route path="/blog" element={
                <Suspense fallback={<PageLoader />}>
                  <Blog />
                </Suspense>
              } />
              <Route path="/blog/:slug" element={
                <Suspense fallback={<PageLoader />}>
                  <BlogPost />
                </Suspense>
              } />
            </Routes>
            <Footer />
          </>
        } />
      </Routes>
      
      {/* Vercel Speed Insights - Non-blocking performance monitoring */}
      <SpeedInsights />
      
      {/* Vercel Analytics - Page view and visitor tracking */}
      <Analytics />
    </div>
  );
}

export default App; 